import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Account } from '@mysaiva/core'
import { ChangePasswordModal, useAuthentication, useLogout } from '@mysaiva/core/authentication'
import { useApplicationContext } from '@mysaiva/core/application'
import { useOrg } from '@mysaiva/core/organizations'
import { userStorage } from '@mysaiva/core/utils'
import { isWorkspaceDeveloperRole } from '../../../utils/developerRedirect'
import { OrgContextCard } from './OrgContextCard'
import { WorkspaceSidebar } from './WorkspaceSidebar'
import { WorkspaceHeader } from './WorkspaceHeader'
import { WorkspaceFooter } from './WorkspaceFooter'
import { BreadcrumbProvider } from './BreadcrumbContext'
import styles from './DeveloperPlatformLayout.module.scss'

/**
 * Developer Platform workspace chrome (SAIV-11348 / SAIV-11493): the
 * always-dark workspace sidebar, a header with breadcrumb, search and mode
 * toggle, the scrolling page area and the compliance footer at the end of
 * it. Child routes render in the `<Outlet />`. The product apps' core
 * `Layout` is intentionally not used: the workspace sidebar has its own
 * behaviour and tighter layout (see WorkspaceSidebar).
 */
export function DeveloperPlatformLayout() {
  const { user } = useAuthentication()
  const { org } = useOrg()
  const { apiClient } = useApplicationContext()
  const { mutate: logout } = useLogout({ apiClient, savedUser: userStorage })
  // Core's AuthenticationProvider owns a change-password modal too, but it
  // sits above the workspace's ThemeProvider and would open light-themed in
  // dark mode (SAIV-11598); the workspace renders its own inside the theme.
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  const account = (
    <Account
      displayName={(user?.username || user?.email) ?? undefined}
      showChangePassword={!!(user?.username && user?.password_expires_at !== undefined)}
      onChangePassword={() => setChangePasswordOpen(true)}
      onLogout={() => logout()}
      variant="light"
    />
  )

  return (
    <BreadcrumbProvider>
      <div className={styles.workspace} data-testid="dp-workspace">
        <WorkspaceSidebar
          organization={<OrgContextCard />}
          account={account}
          showBackToSaiva={!isWorkspaceDeveloperRole(org.user_role_id)}
        />
        <div className={styles.main}>
          <header className={styles.headerBar} data-testid="dp-layout-header">
            <WorkspaceHeader />
          </header>
          <div className={styles.content} data-testid="dp-layout-content">
            <main className={styles.pageSlot}>
              <Outlet />
            </main>
            <div className={styles.footerSlot}>
              <WorkspaceFooter />
            </div>
          </div>
        </div>
      </div>
      <ChangePasswordModal
        open={changePasswordOpen}
        user={user}
        onCancel={() => setChangePasswordOpen(false)}
        onOk={() => setChangePasswordOpen(false)}
      />
    </BreadcrumbProvider>
  )
}
